import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../../environment/environment';
import { bypassAllowed } from '../app/guards/auth-bypass';

const isProduction = environment.production;

const MSALConfig = {
  auth: {
    clientId: environment.MSAL.applicationId,
    authority: environment.MSAL.loginUrl + environment.MSAL.tenantId,
    redirectUri: environment.MSAL.redirectUri,
    postLogoutRedirectUri: environment.MSAL.postLogoutRedirectUri || environment.MSAL.redirectUri,
  },
  cache: {
    cacheLocation: environment.MSAL.cache.cacheLocation,
  },
  system: {
    allowPlatformBroker: false,
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
        }
      },
      logLevel: isProduction ? LogLevel.Error : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
};

const msalInstance = new PublicClientApplication(MSALConfig);

/**
 * MSAL Browser requires `initialize()` to resolve before any other call. Every
 * route sits behind MsalGuard, so the guard and AppComponent's
 * handleRedirectObservable would both start it at once, and v5 does not
 * deduplicate concurrent calls. Redirect responses are still handled inside
 * Angular, where MsalBroadcastService relays their events.
 */
export function initializeMsalInstance(): Promise<void> {
  return msalInstance.initialize();
}

export function MSAL_INSTANCE_FACTORY(): PublicClientApplication {
  return msalInstance;
}

export function MSAL_INTERCEPTOR_CONFIG_FACTORY(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  // Only skip token injection when the opt-in bypass is actually active at
  // app boot. In every other case - production, staging, and local MSAL
  // logins - the API must carry a Bearer token so the backend can resolve
  // the real user identity (otherwise endpoints fall back to the generic
  // "dev" user and per-user data like favourites disappears).
  if (!bypassAllowed()) {
    protectedResourceMap.set(`${environment.apiUrl}/*`, environment.MSAL.scopes);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: true,
  };
}

export function MSAL_GUARD_CONFIG_FACTORY(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.MSAL.scopes],
    },
    // No loginFailedRoute: a failed sign-in is not an authorisation refusal.
    // authGuard keeps the navigation pending and AuthService shows the retry screen.
  };
}
